import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper, { PopperPlacementType } from '@mui/material/Popper'
import { SxProps } from '@mui/material/styles'
import { useT } from '@transifex/react'
import { MouseEvent, ReactNode, useRef, useState } from 'react'
import SplitButtonMenuItem from './SplitButtonMenuItem'
import { SplitButtonActionItem } from './types'

type DropdownButtonVariant = 'outlined' | 'contained' | 'text'

type DropdownButtonProps = {
  elementId: string
  actions: SplitButtonActionItem[]
  label: ReactNode
  startIcon?: ReactNode
  popperPlacement?: PopperPlacementType
  variant?: DropdownButtonVariant
  disabled?: boolean
}

const dropdownButtonStyle: SxProps = {
  mt: 0,
  py: { xs: 0 },
  pl: 3,
  pr: 1,
}

const DropdownButton: React.FC<DropdownButtonProps> = ({
  elementId,
  actions,
  label,
  startIcon,
  popperPlacement,
  variant = 'outlined',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const t = useT()

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (e: Event) => {
    if (anchorRef.current && anchorRef.current.contains(e.target as HTMLElement)) {
      return
    }
    setOpen(false)
  }

  const handleItemClick = (e: MouseEvent, item: SplitButtonActionItem) => {
    if (item.onClick) {
      item.onClick(e)
    }
    setOpen(false)
  }

  return (
    <Box>
      <Button
        id={elementId}
        variant={variant}
        ref={anchorRef}
        onClick={handleToggle}
        startIcon={startIcon}
        endIcon={<ExpandMoreIcon sx={{ mr: 1 }} />}
        disabled={disabled}
        sx={dropdownButtonStyle}
        aria-label={t('Dropdown button')}
        fullWidth>
        {label}
      </Button>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement={popperPlacement}
        transition
        disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper sx={{ maxWidth: { xs: '80vw' } }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id={`${elementId}-Menu`} autoFocusItem>
                  {actions
                    .filter(item => item.visible !== false)
                    .map(item => (
                      <SplitButtonMenuItem
                        id={`${elementId}Menu-${item.key}`}
                        key={item.key}
                        onClick={e => handleItemClick(e, item)}
                        to={item.to}
                        disabled={item.disabled}
                        color={item.color}>
                        {item.name}
                      </SplitButtonMenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  )
}

export default DropdownButton
